import { useContext, useMemo } from 'react';

import { GroupsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';

// Returns a function that tests whether a given userId can view the content
// (report or dashboard), using the same direct-grant rule as useV3HasAccess /
// hasDirectAccess. Reports and dashboards share the v3Access shape, so this is
// content-agnostic.
//
// Known limitation: per-user permission overrides (VIEW_ALL_PRIVATE,
// VIEW_ALL_PUBLISHED) and app/workspace membership grants are not checked here
// — this only evaluates the v3Access field directly. A user whose sole access
// comes from an admin permission would be considered inaccessible.
const useReportAccessForUsers = ({
  content,
}: {
  content: ReportSchedules.ScheduleContent | undefined;
}): ((userId: string) => boolean) => {
  const { groups } = useContext(GroupsContext);

  return useMemo(() => {
    if (!content) return () => true;

    const { v3Access, isV3Published, createdBy } = content;

    return (userId: string): boolean => {
      if (userId === createdBy) return true;
      if (!isV3Published) return false;
      if (v3Access.isPublic) return true;
      if (v3Access.userIds.includes(userId)) return true;
      return v3Access.groupIds.some((gid) => {
        const group = groups.find((g) => g.id === gid);
        return group?.userIds.includes(userId) ?? false;
      });
    };
  }, [groups, content]);
};

export default useReportAccessForUsers;
